import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowLeft, ExternalLink, Loader2, Mail, MessageSquare, PanelRight, Phone } from 'lucide-react';
import { commsApi } from '../phase567Api';
import { MessageList } from '../MessageItem';
import MessageComposer, { sendableChannelsForContact } from '../MessageComposer';
import { formatPhone } from '../shared/phone';
import { fromApi } from '../kit/time';
import usePolling from '../usePolling';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import LivePausedNotice from './LivePausedNotice';
import LoadError from './LoadError';
import ContactPane from './ContactPane';
import MessageDetailsDialog from './MessageDetailsDialog';

const CHIPS = [
  ['all', 'All', null],
  ['sms', 'SMS', ['sms']],
  ['email', 'Email', ['email']],
  ['calls', 'Calls', ['call', 'voicemail']],
];

// Latest sms/email message's channel — the composer's default when the
// contact hasn't been messaged from here before.
function latestTextChannel(messages) {
  const texts = messages.filter((m) => m.channel === 'sms' || m.channel === 'email');
  if (texts.length === 0) return 'sms';
  return texts.reduce((a, b) => (new Date(b.created_at || 0) > new Date(a.created_at || 0) ? b : a)).channel;
}

// Ticket 154: live thread refresh cadence, and how close to the bottom counts
// as "reading the latest" (then new rows scroll into view, else a pill shows).
const THREAD_POLL_MS = 5000;
const NEAR_BOTTOM_PX = 80;

const sameFields = (a, b) => {
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => JSON.stringify(a[k]) === JSON.stringify(b[k]));
};

// Merge a polled page into what is loaded, keyed by `key`. Returns `prev`
// itself when nothing changed so React skips the render (no flicker).
function mergeById(prev, incoming, key) {
  const byId = new Map(prev.map((x, i) => [x[key], i]));
  let next = null;
  const added = [];
  incoming.forEach((x) => {
    const i = byId.get(x[key]);
    if (i === undefined) { added.push(x); return; }
    if (!sameFields(prev[i], x)) { next = next || prev.slice(); next[i] = x; }
  });
  if (!next && added.length === 0) return prev;
  return [...(next || prev), ...added];
}

const byCreated = (a, b) => (fromApi(a.created_at).getTime() || 0) - (fromApi(b.created_at).getTime() || 0)
  || String(a.message_id).localeCompare(String(b.message_id));

// Ticket 142: the per-contact merged timeline. `row` is the contact's Inbox
// list row (may be missing on a direct `?c=` deep link that fell outside the
// current filters) — used for header info and the send-status contact id;
// falls back to the loaded conversations' contact_snapshot.
export default function ContactThread({ contactId, row, transports, live, onBack, onPatchRow, onMoveToTop, onMarkedRead }) {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  // Ticket 195: a failed thread load shows an error with Retry instead of the
  // empty-thread prompt; a failed Load earlier turns its button into Retry.
  const [loadError, setLoadError] = useState(null);
  const [earlierFailed, setEarlierFailed] = useState(false);
  const [chip, setChip] = useState('all');
  const [detailsFor, setDetailsFor] = useState(null);
  // Ticket 195: the contact pane starts open on a wide screen, closed on a narrow one.
  const [paneOpen, setPaneOpen] = useState(() => !!window.matchMedia?.('(min-width: 1024px)')?.matches);
  const [status, setStatus] = useState(null);
  const statusFor = useRef(null);
  // Ids of every message added so far (load, Load earlier, send, poll): lets a
  // poll tell new rows apart without reading state, so its merge can run
  // inside the updater against the true latest list.
  const knownIds = useRef(new Set());
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const boxRef = useRef(null);
  const [showPill, setShowPill] = useState(false);
  // Set by a poll that added rows; consumed after the render that shows them.
  const afterRender = useRef(null);

  // Bumped per load, and on unmount, so a late answer for a superseded load does nothing.
  const loadSeq = useRef(0);
  const loadThread = useCallback(() => {
    const mySeq = ++loadSeq.current;
    const open = () => loadSeq.current === mySeq;
    setLoading(true);
    setLoadError(null);
    commsApi.contactMessages(contactId).then((d) => {
      if (!open()) return;
      const items = d.items || [];
      knownIds.current = new Set(items.map((m) => m.message_id));
      setMessages(items);
      setNextBefore(d.next_before || null);
      setConversations(d.conversations || []);
      const hasUnread = row ? (row.unread || 0) > 0 : (d.conversations || []).some((c) => (c.unread_count || 0) > 0);
      if (hasUnread) {
        // Not gated on `alive`: the server marks the contact read even if the
        // user has already moved on, so the list row must follow either way.
        commsApi.markContactRead(contactId).then(() => onMarkedRead(contactId)).catch(() => {});
      }
    }).catch((e) => {
      if (!open()) return;
      setMessages([]);
      setConversations([]);
      setNextBefore(null);
      setLoadError(errorMessage(e, 'Could not load this conversation'));
    }).finally(() => { if (open()) setLoading(false); });
    // Only re-run when the open contact changes — row/onMarkedRead read the
    // value current at open time, not on every parent patch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);
  useEffect(() => {
    setChip('all');
    setDetailsFor(null);
    loadThread();
    return () => { loadSeq.current += 1; };
  }, [loadThread]);

  const loadStatus = useCallback(() => {
    commsApi.sendStatus(contactId).then((st) => { if (statusFor.current === contactId) setStatus(st); }).catch(() => {});
  }, [contactId]);
  useEffect(() => { setStatus(null); statusFor.current = contactId; loadStatus(); }, [contactId, loadStatus]);

  // Ticket 154: refresh the newest page every 5 s and merge it in. Never
  // touches nextBefore, so rows from "Load earlier" stay put.
  const pollMessages = useCallback(async () => {
    const d = await commsApi.contactMessages(contactId, { limit: 50 });
    // The thread is keyed by contact: a late answer for a closed one must do nothing.
    if (!alive.current) return;
    const incoming = d.items || [];
    const fresh = incoming.filter((m) => !knownIds.current.has(m.message_id));
    fresh.forEach((m) => knownIds.current.add(m.message_id));
    if (fresh.length) {
      const box = boxRef.current;
      const near = !box || box.scrollHeight - box.scrollTop - box.clientHeight <= NEAR_BOTTOM_PX;
      afterRender.current = near ? 'bottom' : 'pill';
    }
    setMessages((prev) => {
      const merged = mergeById(prev, incoming, 'message_id');
      return merged !== prev && fresh.length ? merged.slice().sort(byCreated) : merged;
    });
    const inbound = fresh.filter((m) => m.direction === 'inbound').sort(byCreated);
    if (inbound.length && document.visibilityState !== 'hidden') {
      const latest = inbound[inbound.length - 1];
      commsApi.markContactRead(contactId).then(() => onMarkedRead(contactId)).catch(() => {});
      const preview = latest.channel === 'call' ? 'Call' : latest.channel === 'voicemail' ? 'Voicemail' : (latest.body || '');
      onPatchRow(contactId, { last_preview: preview, latest_at: latest.created_at, last_channel: latest.channel });
      onMoveToTop(contactId);
    }
    setConversations((prevConvs) => mergeById(prevConvs, d.conversations || [], 'conversation_id'));
  }, [contactId, onMarkedRead, onPatchRow, onMoveToTop]);
  // immediate: false — the initial load above is the first fetch.
  // Paused on a failed load: there is nothing loaded to refresh, and Retry is the way back.
  usePolling(pollMessages, THREAD_POLL_MS, { enabled: !loading && !loadError, immediate: false });

  useLayoutEffect(() => {
    const action = afterRender.current;
    afterRender.current = null;
    const box = boxRef.current;
    if (!action || !box) return;
    if (action === 'bottom') { box.scrollTop = box.scrollHeight; setShowPill(false); } else setShowPill(true);
  }, [messages]);

  const scrollToBottom = () => {
    const box = boxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
    setShowPill(false);
  };
  const onScroll = () => {
    const box = boxRef.current;
    if (showPill && box && box.scrollHeight - box.scrollTop - box.clientHeight <= NEAR_BOTTOM_PX) setShowPill(false);
  };

  const loadEarlier = async () => {
    if (!nextBefore || loadingEarlier) return;
    setLoadingEarlier(true);
    setEarlierFailed(false);
    try {
      const d = await commsApi.contactMessages(contactId, { before: nextBefore });
      // Skip rows a poll already added while this request was in flight.
      const older = (d.items || []).filter((m) => !knownIds.current.has(m.message_id));
      older.forEach((m) => knownIds.current.add(m.message_id));
      setMessages((prev) => [...older, ...prev]);
      setNextBefore(d.next_before || null);
    } catch (e) {
      // nextBefore stays as-is, so the button (now "Retry") asks for the same page again.
      setEarlierFailed(true);
      toast({ title: 'Could not load earlier messages', description: errorMessage(e) });
    } finally { setLoadingEarlier(false); }
  };

  const convFor = useCallback(async (channel) => {
    const existing = conversations.find((c) => c.channel === channel);
    if (existing) return existing;
    const created = await commsApi.createConversation(contactId, channel);
    setConversations((prev) => (prev.some((c) => c.conversation_id === created.conversation_id) ? prev : [...prev, created]));
    return created;
  }, [conversations, contactId]);

  const isGhl = row?.source === 'ghl' || conversations.some((c) => c.source === 'ghl');
  const channels = sendableChannelsForContact(isGhl, transports);
  const defaultChannel = channels.includes(latestTextChannel(messages)) ? latestTextChannel(messages) : (channels[0] || 'sms');

  const onComposerSent = (m, targetConv) => {
    knownIds.current.add(m.message_id);
    setMessages((prev) => [...prev, m]);
    const preview = m.channel === 'call' ? 'Call' : m.channel === 'voicemail' ? 'Voicemail' : (m.body || '');
    onPatchRow(contactId, {
      last_preview: preview,
      latest_at: m.created_at,
      last_channel: m.channel,
      channels: Array.from(new Set([...(row?.channels || []), m.channel])),
      source: (targetConv?.source === 'ghl' || row?.source === 'ghl') ? 'ghl' : (row?.source || targetConv?.source || 'bfy'),
    });
    onMoveToTop(contactId);
  };

  const snapshot = conversations[0]?.contact_snapshot;
  const name = row?.display_name || snapshot?.display_name || (row?.phone ? formatPhone(row.phone) : null) || contactId;
  const phone = row?.phone || snapshot?.phones?.[0]?.value;
  const email = row?.email || snapshot?.emails?.[0]?.value;

  const visible = chip === 'all' ? messages : messages.filter((m) => CHIPS.find(([k]) => k === chip)[2].includes(m.channel));

  return (
    <div className="md:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-sm flex flex-col h-full min-h-0 overflow-hidden" data-testid="contact-thread">
      <LivePausedNotice live={live} />
      <div className="p-3 border-b border-slate-100 flex items-start gap-2">
        <button type="button" onClick={onBack} className="md:hidden text-slate-500 mt-0.5" data-testid="thread-back" aria-label="Back to list">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <Link to={`/portal/contacts/${contactId}`} className="block text-sm font-medium truncate hover:underline" data-testid="thread-name-link">{name}</Link>
          <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5 flex-wrap">
            {phone && <span className="flex items-center gap-1"><Phone size={11} /> {formatPhone(phone)}</span>}
            {email && <span className="flex items-center gap-1"><Mail size={11} /> {email}</span>}
          </div>
        </div>
        <Link to={`/portal/contacts/${contactId}`} className="text-xs text-slate-500 hover:underline flex items-center gap-1 flex-shrink-0" data-testid="thread-open-contact">
          Open contact <ExternalLink size={11} />
        </Link>
        <button type="button" onClick={() => setPaneOpen((v) => !v)} aria-pressed={paneOpen} aria-label="Contact details"
          title="Contact details" data-testid="thread-pane-toggle"
          className={`flex-shrink-0 p-1 rounded ${paneOpen ? 'bg-slate-100 text-[#070B5F]' : 'text-slate-500 hover:bg-slate-50'}`}>
          <PanelRight size={16} />
        </button>
      </div>

      <div className="flex-1 min-h-0 flex relative">
      <div className="flex-1 min-w-0 flex flex-col min-h-0">

      <div className="p-2 border-b border-slate-100 flex items-center gap-1.5 flex-wrap" role="tablist">
        {CHIPS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={chip === key}
            onClick={() => setChip(key)}
            data-testid={`thread-chip-${key}`}
            className={`px-2.5 py-1 rounded-full text-xs border ${chip === key ? 'border-[#070B5F] bg-[#070B5F]/5 text-[#070B5F]' : 'border-slate-200 text-slate-500'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* relative wrapper: the pill floats over the message area only, never the composer. */}
      <div className="flex-1 min-h-0 relative flex flex-col">
      <div ref={boxRef} onScroll={onScroll} className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2" data-testid="thread-messages">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-400">
            <Loader2 className="animate-spin" size={16} />
          </div>
        ) : loadError ? (
          <LoadError message={loadError} onRetry={loadThread} testId="thread-error" />
        ) : (
          <>
            {nextBefore && (
              <div className="text-center pb-2">
                <button
                  type="button"
                  onClick={loadEarlier}
                  disabled={loadingEarlier}
                  data-testid="thread-load-earlier"
                  className="text-xs font-medium text-slate-500 hover:text-slate-700 disabled:opacity-50"
                >
                  {loadingEarlier ? <Loader2 className="animate-spin inline" size={14} /> : earlierFailed ? 'Retry' : 'Load earlier'}
                </button>
              </div>
            )}
            {visible.length === 0 ? (
              <div className="text-center text-slate-400 text-sm p-8">
                <MessageSquare size={28} className="mx-auto mb-2 text-slate-300" />
                No messages yet — send one below.
              </div>
            ) : <MessageList messages={visible} contactId={contactId} contactName={name} onDetails={setDetailsFor} />}
          </>
        )}
      </div>
      {showPill && (
        <button
          type="button"
          onClick={scrollToBottom}
          data-testid="thread-new-pill"
          className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-white shadow-md"
          style={{ background: '#070B5F' }}
        >
          <ArrowDown size={12} /> New message
        </button>
      )}
      </div>

      {/* Mounted only once messages are in: the composer reads its default
          channel once, at mount, so mounting mid-load would pin it to SMS. */}
      {!loading && !loadError && (
      <MessageComposer
        key={contactId}
        conv={{ source: isGhl ? 'ghl' : undefined, channel: defaultChannel }}
        channels={channels}
        convFor={convFor}
        transports={transports}
        status={status}
        onSent={onComposerSent}
        onBlocked={loadStatus}
      />
      )}
      </div>
      {paneOpen && (
        <ContactPane
          contactId={contactId}
          row={row}
          fallback={snapshot}
          name={name}
          className="flex absolute inset-y-0 right-0 z-10 w-72 border-l border-slate-100 shadow-lg lg:static lg:z-auto lg:w-64 lg:shadow-none flex-shrink-0"
        />
      )}
      </div>
      <MessageDetailsDialog message={detailsFor} contact={{ name, phone, email }} onOpenChange={(o) => { if (!o) setDetailsFor(null); }} />
    </div>
  );
}
